import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink, Loader2, Package, ShoppingBag } from 'lucide-react';

import {
    formatCrossDockPurchaseOrderPoMapLabel,
    formatCrossDockPurchaseOrderScheduledDelivery,
    getCrossDockPurchaseOrderPoMapIdLabel,
    getCrossDockPurchaseOrderPoMapSelectionKey,
    isPackagingCrossDockPurchaseOrderPoMap,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import { formatPurchaseOrderDateTime } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatCurrency,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { PaginationControls, ResultsCounter } from 'shared';

const SERVICE_FEE = 'SERVICE_FEE';
const SHIPPING_FEE = 'SHIPPING_FEE';

function feeTotal(additionalFees, chargeType) {
    const match = (additionalFees ?? []).find((fee) => fee.chargeType === chargeType);
    if (match?.totalRate == null) {
        return null;
    }
    const numeric = Number(match.totalRate);
    return Number.isFinite(numeric) ? numeric : null;
}

function additionalFeesTotal(additionalFees) {
    return (additionalFees ?? []).reduce((sum, fee) => {
        if (fee.chargeType === SERVICE_FEE || fee.chargeType === SHIPPING_FEE) {
            return sum;
        }
        const numeric = Number(fee.totalRate ?? 0);
        return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
}

function formatFee(value) {
    return value == null ? FALLBACK_TEXT : formatCurrency(value);
}

function PoNumbersCell({ poMaps }) {
    const rows = (poMaps ?? []).filter((poMap) => getCrossDockPurchaseOrderPoMapIdLabel(poMap) !== FALLBACK_TEXT);

    if (rows.length === 0) {
        return FALLBACK_TEXT;
    }

    return (
        <div className="flex max-w-[16rem] flex-wrap gap-1.5">
            {rows.map((poMap) => {
                const idLabel = getCrossDockPurchaseOrderPoMapIdLabel(poMap);
                const fullLabel = formatCrossDockPurchaseOrderPoMapLabel(poMap);
                const key = getCrossDockPurchaseOrderPoMapSelectionKey(poMap) ?? idLabel;
                const Icon = isPackagingCrossDockPurchaseOrderPoMap(poMap) ? Package : ShoppingBag;

                return (
                    <Badge
                        key={key}
                        variant="secondary"
                        className="max-w-full gap-1 font-medium text-foreground"
                        title={fullLabel}
                    >
                        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span className="truncate">{idLabel}</span>
                    </Badge>
                );
            })}
        </div>
    );
}

const renderStatusCell = ({ getValue }) => <PackagingPOStatusCell status={getValue()} fallbackText={FALLBACK_TEXT} />;

const renderPoNumbersCell = ({ getValue }) => <PoNumbersCell poMaps={getValue()} />;

const renderActionsCell = ({ row, table }) => {
    const onViewRow = table.options.meta?.onViewRow;
    const entityId = row.original?.id;
    if (!entityId || !onViewRow) {
        return FALLBACK_TEXT;
    }
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`View cross dock purchase order ${entityId}`}
            onClick={() => onViewRow(row.original)}
        >
            <ExternalLink className="h-4 w-4" />
        </Button>
    );
};

/**
 * @param {Object} props
 * @param {Array<Object>} [props.data=[]]
 * @param {Map<number, string>} [props.facilityNameById]
 * @param {Map<number, string>} [props.crossDockNameById]
 * @param {number} [props.totalCount=0]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {boolean} [props.hasNextPage=false]
 * @param {boolean} [props.hasPrevious=false]
 * @param {() => void} [props.onPrevious]
 * @param {() => void} [props.onNext]
 * @param {number} [props.currentPage]
 * @param {number} [props.totalPages]
 * @param {number} [props.pageSize]
 * @param {(size: number) => void} [props.onPageSizeChange]
 * @param {number[]} [props.pageSizeOptions]
 * @param {(row: Object) => void} [props.onViewRow]
 */
export function CrossDockPurchaseOrdersTable({
    crossDockNameById,
    currentPage,
    data = [],
    facilityNameById,
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    onNext,
    onPageSizeChange,
    onPrevious,
    onViewRow,
    pageSize,
    pageSizeOptions,
    totalCount = 0,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
                cell: ({ getValue }) => getValue() ?? FALLBACK_TEXT,
            },
            {
                header: 'Facility',
                id: 'facility',
                accessorFn: (row) => facilityNameById?.get(Number(row.facilityId)) ?? row.facilityId,
                cell: ({ getValue }) => getValue() ?? FALLBACK_TEXT,
            },
            {
                header: 'PO Number(s)',
                id: 'poNumbers',
                accessorFn: (row) => row.poMaps,
                cell: renderPoNumbersCell,
            },
            {
                header: 'Created On',
                accessorKey: 'createdAt',
                cell: ({ getValue }) => formatPurchaseOrderDateTime(getValue()),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: renderStatusCell,
            },
            {
                header: 'Destination Cross Dock',
                id: 'crossDock',
                accessorFn: (row) => crossDockNameById?.get(Number(row.crossDockId)) ?? row.crossDockId,
                cell: ({ getValue }) => getValue() ?? FALLBACK_TEXT,
            },
            {
                header: 'Scheduled Delivery',
                id: 'scheduledDelivery',
                accessorFn: (row) => formatCrossDockPurchaseOrderScheduledDelivery(row),
            },
            {
                header: 'Service Fee',
                id: 'serviceFee',
                accessorFn: (row) => feeTotal(row.additionalFees, SERVICE_FEE),
                cell: ({ getValue }) => formatFee(getValue()),
            },
            {
                header: 'Additional Fees',
                id: 'additionalFees',
                accessorFn: (row) => additionalFeesTotal(row.additionalFees),
                cell: ({ getValue }) => {
                    const total = getValue();
                    return total ? formatFee(total) : FALLBACK_TEXT;
                },
            },
            {
                header: 'Shipping Fee',
                id: 'shippingFee',
                accessorFn: (row) => feeTotal(row.additionalFees, SHIPPING_FEE),
                cell: ({ getValue }) => formatFee(getValue()),
            },
            {
                header: 'Actions',
                id: 'actions',
                cell: renderActionsCell,
            },
        ],
        [crossDockNameById, facilityNameById],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        meta: {
            onViewRow,
        },
    });

    const showPagination = totalCount > 0 && (hasPrevious || hasNextPage);

    return (
        <div className="flex flex-col gap-4">
            <ResultsCounter
                loadedCount={tableData.length}
                totalCount={totalCount}
                isRefreshing={isFetching && !isLoading}
            />
            <div className="relative rounded-md border">
                {(isLoading || isFetching) && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                )}
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className="whitespace-normal break-words">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No cross dock purchase orders found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {showPagination ? (
                <PaginationControls
                    hasPrevious={hasPrevious}
                    hasNext={hasNextPage}
                    onPrevious={onPrevious}
                    onNext={onNext}
                    isLoading={isFetching}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : null}
        </div>
    );
}
